import { resolveDesktopModuleDatefieldDateFilterParameter } from 'src/stores/_infrastructure/dateTimeRangeQueryValue';

import { DateRangeSelection } from 'src/models/common';
import { GenericActivityListPagination, GenericActivityListSort } from 'src/models/genericActivityList';
import { SharedQueryIdoDocumentBinding } from 'src/models/organization';
import { AbstractQuery } from 'src/models/query';

import { GetTheIdoDocumentModuleIdoDocumentsForTheIdoQuery } from './idoDocumentQueries';

export class GetTheDocumentModuleBoxesForTheUserQuery extends AbstractQuery {
	name = 'IFS_DesktopModule_Document_Query_Queries_GetTheDocumentModuleBoxesForTheUserQuery';
	page = 1;
	subscribeForUpdates = false;
}

export class GetTheStandardDocumentModuleBoxForTheUserQuery extends AbstractQuery {
	name = 'IFS_DesktopModule_Document_Query_Queries_GetTheStandardDocumentModuleBoxForTheUserQuery';
	page = 1;
	subscribeForUpdates = false;
}

export class GetTheDocumentModuleDocumentsForTheUserQuery extends AbstractQuery {
	limit = 100;
	name = 'IFS_DesktopModule_Document_Query_Queries_GetTheDocumentModuleDocumentsForTheUserQuery';
	page = 1;
	sort: Array<GenericActivityListSort> = [
		{
			direction: 'DESC',
			property: 'timestamp',
		},
	];
	standardFilterSelection = '';

	constructor(
		view: string,
		date: DateRangeSelection | string,
		boxIdFilter: Array<string>,
		userIdFilter: Array<number>,
		searchTerm: string,
		hiddenSearchTerm: string,
		pagination: GenericActivityListPagination,
		sort: GenericActivityListSort,
		suppressBoxCustomCoupling = false
	) {
		super();

		this.page = pagination.page;
		this.limit = pagination.rowsPerPage;
		this.sort = [sort];

		if (view === 'open') {
			this.standardFilterSelection = 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonUnfinished';
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonUnfinished',
			});
		} else if (view === 'all') {
			this.standardFilterSelection = 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonAll';
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonAll',
			});
		} else if (view === 'mine') {
			this.standardFilterSelection = 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonMine';
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonMine',
			});
		} else if (view === 'custom') {
			this.standardFilterSelection = 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonCustom';
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonCustom',
			});
		}

		this.parameters.push({
			DesktopModuleToolbarButtonGlobalizationFilter: true,
		});
		this.parameters.push({ DesktopModuleToolbarButtonArchivFilter: false });

		if (searchTerm !== '' && searchTerm !== undefined) {
			this.parameters.push({
				DesktopModuleToolbarTextfieldSearchField: searchTerm,
			});

			this.parameters.push({
				DesktopModuleToolbarHiddenfieldSearchField: hiddenSearchTerm,
			});

			this.parameters.push({
				DesktopModuleToolbarDatefieldDateFilter:
					'DesktopModuleToolbarSegmentedButtonTimeRangeSelectionButtonAll',
			});
		} else {
			this.parameters.push({
				DesktopModuleToolbarDatefieldDateFilter: resolveDesktopModuleDatefieldDateFilterParameter(date),
			});
		}

		this.disableSubscriptionsWhileSearchIsActive(searchTerm, hiddenSearchTerm);

		// Task 3.1f: die Box→Custom-Kopplung kollabiert alle drei Ansichts-Slices (Offen/Meine/Alle) auf dieselbe
		// Custom-Query, sobald Boxen ohne User-Filter aktiv sind (Backend: last-wins auf StandardFilterSelectionButton
		// → 8/8/8). Isolierte gespeicherte-Listen-Tabs unterdrücken die Kopplung, damit jede Ansicht view-scoped mit
		// den Listen-Boxen schneidet. Default-Store ('documentList') behält die klassische Custom-Kollaps-Kopplung (byte-
		// identisch). Backend-Schnittmenge nur für taskList wire-verifiziert; übrige Module als analog angenommen.
		if (!suppressBoxCustomCoupling && boxIdFilter.length > 0 && userIdFilter.length == 0) {
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonCustom',
			});
		}

		this.parameters.push({ DesktopModuleCustomFilterCondition: {} });

		this.parameters.push({
			DesktopModuleToolbarCycleButtonDirection: 'DesktopModuleToolbarCycleButtonDirectionBidirectional',
		});

		this.parameters.push({ DesktopModuleBoxIds: boxIdFilter });

		this.parameters.push({
			DesktopModuleToolbarSplitButtonUserFilter: userIdFilter,
		});
	}

	uniqueQueryIdentifier(): string {
		return this.createStableIdentifier({
			standardFilterSelection: this.standardFilterSelection,
		});
	}
}

export const createDocumentListQueryForView = (payload: {
	binding: SharedQueryIdoDocumentBinding | null;
	boxIdFilter: Array<string>;
	date: DateRangeSelection | string;
	hiddenSearchTerm: string;
	pagination: GenericActivityListPagination;
	searchTerm: string;
	sort: GenericActivityListSort;
	suppressBoxCustomCoupling?: boolean;
	userIdFilter: Array<number>;
	view: 'all' | 'mine' | 'open';
}): AbstractQuery => {
	if (payload.binding !== null && payload.binding.idoLinkings.length > 0) {
		const linking = payload.binding.idoLinkings[0];

		return new GetTheIdoDocumentModuleIdoDocumentsForTheIdoQuery({
			boxIds: payload.boxIdFilter,
			categoryFilter: payload.binding.categoryFilter,
			date: payload.date,
			descending: payload.sort.direction === 'DESC',
			idoId: linking.idoId,
			idoInstanceId: linking.instanceId,
			page: payload.pagination.page,
			rowsPerPage: payload.pagination.rowsPerPage,
			searchTerm: payload.searchTerm,
			sortBy: payload.sort.property,
			userIds: payload.userIdFilter,
			view: payload.view,
		});
	}

	return new GetTheDocumentModuleDocumentsForTheUserQuery(
		payload.view,
		payload.date,
		payload.boxIdFilter,
		payload.userIdFilter,
		payload.searchTerm,
		payload.hiddenSearchTerm,
		payload.pagination,
		payload.sort,
		payload.suppressBoxCustomCoupling ?? false
	);
};
